import { Button, Classes } from '@blueprintjs/core';
import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { ColorPicker } from 'src/components/ColorPicker';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramAttributesTable } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTable';
import { ProjectDiagramClassDrawerRelationships } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationships';
import { ClassRelationshipItem } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useClickModelConceptAttributeLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptAttributeLink';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useClickModelConceptRelationshipLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptRelationshipLink';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import {
  useAddAttributeToClass,
  useRemoveAttributeFromClass,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptAttributeForClass';
import {
  useAddHierarchyForClass,
  useRemoveHierarchyForClass,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptHierarchyForClass';
import {
  useAddRelationshipForClass,
  useRemoveRelationshipForClass,
  useUpdateRelationshipForClass,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptRelationshipForClass';
import { useUpdateAttribute } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateAttribute';
import { useUpdateColorId } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateColorId';
import { useUpdateDescriptionForIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateDescriptionForIri';
import { useUpdateIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateIri';
import { useUpdateLabel } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateLabel';
import { useRemoveModelConceptClass } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelConceptClass';
import {
  ModelConceptClass,
  ModelConceptClassHierarchy,
} from 'src/containers/ProjectDiagram/types/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getConnectedHierarchyDataForClass } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/getConnectedHierarchyDataForClass';
import { getConnectedRelationshipDataForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/getConnectedRelationshipDataForClass';
import { classDraftChangesSelector } from 'src/selectors/draftClass';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { entityDrawerNodeDataSelector } from 'src/selectors/entityDrawerNodeData';
import { projectClassAttributeItemsSelector } from 'src/selectors/projectClassAttributesItems';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectColorIdsByIriSelector } from 'src/selectors/projectColorIdsByIri';
import { projectDescriptionsByIriSelector } from 'src/selectors/projectDescriptionsByIri';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';
import { projectRelationshipItemsSelector } from 'src/selectors/projectRelationshipItems';
import { EntityDrawerType } from 'src/store/ui/types';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramClassDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();
  const drawer = useReduxSelector(entityDrawerSelector);

  const isEditingAttributes =
    drawer.type === EntityDrawerType.CLASS && drawer?.isEditingAttributes;

  const allClassItems = useReduxSelector(
    projectClassItemsSelector(projectId),
    isEqual
  );

  const allRelationshipItems = useReduxSelector(
    projectRelationshipItemsSelector(projectId),
    isEqual
  );

  const colorIdsByIri = useReduxSelector(
    projectColorIdsByIriSelector(projectId),
    isEqual
  );

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );

  const descriptionsByIri = useReduxSelector(
    projectDescriptionsByIriSelector(projectId),
    isEqual
  );

  const linkDataArray = useReduxSelector(
    projectLinkDataArraySelector(projectId),
    isEqual
  );

  const nodeData = useReduxSelector(
    entityDrawerNodeDataSelector<ModelConceptClass>(projectId),
    isEqual
  );

  const {
    addedChildren,
    addedParents,
    isColorIdModified,
    isDescriptionModified,
    isLabelModified,
    modifiedRelationships,
  } = useReduxSelector(
    classDraftChangesSelector(projectId, nodeData?.value),
    isEqual
  );

  const hierarchySelectItems = React.useMemo(() => {
    const parentIris = new Set<string>();
    const childIris = new Set<string>();

    const hierarchyDataArray = nodeData
      ? getConnectedHierarchyDataForClass({ linkDataArray }, nodeData.value)
      : [];

    hierarchyDataArray.forEach((linkData) => {
      const isParent = linkData.to === nodeData?.key;
      const isChild = linkData.from === nodeData?.key;
      const classIri = getModelConceptClassValue(
        isParent ? linkData.from : linkData.to
      );

      if (isParent) {
        childIris.add(classIri);
      }

      if (isChild) {
        parentIris.add(classIri);
      }
    });

    return {
      currentParents: allClassItems.filter((item) =>
        parentIris.has(item.value)
      ),
      availableParents: allClassItems.filter(
        (item) => !parentIris.has(item.value)
      ),
      currentChildren: allClassItems.filter((item) =>
        childIris.has(item.value)
      ),
      availableChildren: allClassItems.filter(
        (item) => !childIris.has(item.value)
      ),
    };
  }, [allClassItems, linkDataArray, nodeData]);

  const relationships = React.useMemo<ClassRelationshipItem[]>(() => {
    if (!nodeData) {
      return [];
    }

    const relDataArray = getConnectedRelationshipDataForClass(
      { linkDataArray },
      nodeData.value
    );

    return allRelationshipItems.reduce<ClassRelationshipItem[]>(
      (acc, relationship) => {
        const directions: RelationshipDirection[] = [];
        if (
          relDataArray.some(
            (relData) =>
              relData.value === relationship.value &&
              relData.from === nodeData.key
          )
        ) {
          directions.push(RelationshipDirection.OUTGOING);
        }
        if (
          relDataArray.some(
            (relData) =>
              relData.value === relationship.value &&
              relData.to === nodeData.key
          )
        ) {
          directions.push(RelationshipDirection.INCOMING);
        }

        if (directions.length) {
          acc.push({
            ...relationship,
            directions,
            highlighted: modifiedRelationships.has(relationship.value),
          });
        }
        return acc;
      },
      []
    );
  }, [allRelationshipItems, linkDataArray, modifiedRelationships, nodeData]);

  const attributes = useReduxSelector(
    projectClassAttributeItemsSelector(projectId, nodeData?.value || '')
  );

  const handleUpdateIri = useUpdateIri(nodeData?.value);

  const handleUpdateLabel = useUpdateLabel(nodeData?.value);

  const handleUpdateColorId = useUpdateColorId(nodeData?.value);

  const handleUpdateDescription = useUpdateDescriptionForIri(nodeData?.value);

  const handleRemoveClass = useRemoveModelConceptClass();

  // hierarchies
  const handleClickClass = useClickModelConceptClassLink();

  const createNewClassFromQuery = useCreateModelConceptClassFromQuery();

  const handleAddParent = useAddHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.PARENTS,
  });

  const handleAddChild = useAddHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.CHILDREN,
  });

  const handleRemoveParent = useRemoveHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.PARENTS,
  });

  const handleRemoveChild = useRemoveHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.CHILDREN,
  });

  // relationships
  const handleAddRelationship = useAddRelationshipForClass({
    classIri: nodeData?.value,
  });

  const handleClickRelationship = useClickModelConceptRelationshipLink();

  const handleUnlinkRelationship = useRemoveRelationshipForClass({
    classIri: nodeData?.value,
  });

  const handleUpdateRelationship = useUpdateRelationshipForClass({
    classIri: nodeData?.value,
  });

  // attributes
  const handleAddAttribute = useAddAttributeToClass(nodeData?.value);

  const handleClickAttributeLink = useClickModelConceptAttributeLink();

  const handleUnlinkAttribute = useRemoveAttributeFromClass(nodeData?.value);

  const handleUpdateAttribute = useUpdateAttribute();

  if (!nodeData) {
    return null;
  }

  const colorId = colorIdsByIri[nodeData.value] || '';
  const descriptionForIri = descriptionsByIri[nodeData.value] || '';
  const label = labelsByIri[nodeData.value];

  if (isEditingAttributes) {
    return (
      <div className={Classes.DRAWER_BODY}>
        <ProjectDiagramAttributesTable
          attributes={attributes}
          onAddAttribute={handleAddAttribute}
          onClickLink={handleClickAttributeLink}
          onUnlinkAttribute={handleUnlinkAttribute}
          onUpdateAttribute={handleUpdateAttribute}
        />
      </div>
    );
  }

  return (
    <>
      <div className={Classes.DRAWER_BODY}>
        <ProjectDiagramEditableIri
          header={
            isEditingAttributes
              ? copy.components.projectDiagram.classDrawer.nameWithAttributes
              : copy.components.projectDiagram.classDrawer.name
          }
          iri={nodeData.value}
          isHighlighted={isLabelModified}
          label={label}
          onSetIri={handleUpdateIri}
          onSetLabel={handleUpdateLabel}
          placeholder={
            label === ''
              ? copy.components.projectDiagram.modelConceptClass.new
              : ''
          }
          rightEl={
            <ColorPicker
              colorId={colorId}
              highlighted={isColorIdModified}
              onSetColorId={handleUpdateColorId}
            />
          }
          testId={TEST_IDS.projectDiagram.entityDrawer.name}
        />
        <ProjectDiagramEditableTextArea
          // key here to description changes when you change from drawer to drawer
          key={`${nodeData.value}-description`}
          header={copy.components.projectDiagram.classDrawer.description}
          isHighlighted={isDescriptionModified}
          onSetValue={handleUpdateDescription}
          value={descriptionForIri}
        />

        <div className="sd-drawer-project-diagram-entity-columns">
          <SelectableLinks
            buttonText=""
            creatable
            createNewItemFromQuery={createNewClassFromQuery}
            createNewItemRenderer={createNewSelectItemRenderer}
            createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
              ''
            )}
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.classDrawer.parentClasses}
            highlightedValues={addedParents}
            items={hierarchySelectItems.availableParents}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                ModelConceptClassHierarchy.PARENTS,
                idx
              )
            }
            onClick={handleClickClass}
            onRemove={handleRemoveParent}
            onSelect={handleAddParent}
            selected={hierarchySelectItems.currentParents}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              ModelConceptClassHierarchy.PARENTS
            )}
          />
          <SelectableLinks
            buttonText=""
            creatable
            createNewItemFromQuery={createNewClassFromQuery}
            createNewItemRenderer={createNewSelectItemRenderer}
            createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
              ''
            )}
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.classDrawer.childClasses}
            highlightedValues={addedChildren}
            items={hierarchySelectItems.availableChildren}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                ModelConceptClassHierarchy.CHILDREN,
                idx
              )
            }
            onClick={handleClickClass}
            onRemove={handleRemoveChild}
            onSelect={handleAddChild}
            selected={hierarchySelectItems.currentChildren}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              ModelConceptClassHierarchy.CHILDREN
            )}
          />
        </div>

        <ProjectDiagramClassDrawerRelationships
          onAddRelationship={handleAddRelationship}
          onClickRelationship={handleClickRelationship}
          onUnlinkRelationship={handleUnlinkRelationship}
          onUpdateRelationship={handleUpdateRelationship}
          relationships={relationships}
        />

        <ProjectDiagramAttributesTable
          attributes={attributes}
          onAddAttribute={handleAddAttribute}
          onClickLink={handleClickAttributeLink}
          onUnlinkAttribute={handleUnlinkAttribute}
          onUpdateAttribute={handleUpdateAttribute}
        />
      </div>

      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectDiagram.entityDrawer.removeButton}
            onClick={() => handleRemoveClass({ key: nodeData.key })}
            outlined
          >
            {copy.components.projectDiagram.entityDrawerButtons.remove}
          </Button>
        </div>
      </div>
    </>
  );
};
